/**
 * Mock ITR assessments for dev / mock mode.
 *
 * Mirrors the assessment shapes that used to be generated inline in
 * `mockCustomers.ts`, but pulled out into a standalone, deterministic fixture
 * keyed by mock customer id (cust-001 …). The assessments service reads this in
 * mock mode; the customer mock data keeps its own generated copies for the
 * header stat counts.
 */

import type { Assessment, ITRType, AssessmentStatus, TaxRegime } from '@/types/assessment.types'

const CURRENT_YEAR = 2025

const FILED_LIKE: AssessmentStatus[] = ['filed', 'processed', 'refund_issued']

function fyString(startYear: number): string {
  return `${startYear}-${(startYear + 1).toString().slice(-2)}`
}

function ayString(startYear: number): string {
  return `${startYear + 1}-${(startYear + 2).toString().slice(-2)}`
}

function sourceVersionId(customerId: string, index: number): string {
  const customerNumber = Number(customerId.match(/\d+$/)?.[0] ?? 0)
  return `00000000-0000-4000-8000-${String(customerNumber).padStart(6, '0')}${String(index + 1).padStart(6, '0')}`
}

interface SeedSpec {
  itrType: ITRType
  status: AssessmentStatus
  regime: TaxRegime
  taxableIncome: number
  taxPaid: number
  refundAmount?: number
  demandAmount?: number
}

/**
 * Build a deterministic list of assessments for one customer. The first entry
 * is the most recent (open) year; older years are filed/processed.
 */
function buildAssessments(customerId: string, pan: string, specs: SeedSpec[]): Assessment[] {
  return specs.map((spec, i) => {
    const fy = CURRENT_YEAR - i - 1
    const filed = FILED_LIKE.includes(spec.status)
    return {
      id: `asmt-${customerId}-${i + 1}`,
      sourceVersionId: sourceVersionId(customerId, i),
      customerId,
      pan,
      financialYear: fyString(fy),
      assessmentYear: ayString(fy),
      itrType: spec.itrType,
      taxRegime: spec.regime,
      status: spec.status,
      dueDate: `${fy + 1}-07-31`,
      filedDate: filed ? `${fy + 1}-07-${String((i % 20) + 1).padStart(2, '0')}` : undefined,
      acknowledgeNumber: filed ? `ACK${fy + 1}${customerId.replace(/\D/g, '')}${i + 1}` : undefined,
      grossIncome: spec.taxableIncome + 150000,
      grossTotalIncome: spec.taxableIncome + 150000,
      totalDeductions: 150000,
      taxableIncome: spec.taxableIncome,
      taxPaid: spec.taxPaid,
      refundAmount: spec.refundAmount,
      demandAmount: spec.demandAmount,
      createdDate: `${fy + 1}-04-01T09:00:00.000Z`,
      lastUpdated: `${fy + 1}-07-15T12:30:00.000Z`,
    }
  })
}

export const mockAssessments: Assessment[] = [
  ...buildAssessments('cust-001', 'ABCDE1234F', [
    { itrType: 'ITR-1', status: 'in_progress', regime: 'new', taxableIncome: 1250000, taxPaid: 90000 },
    { itrType: 'ITR-1', status: 'processed', regime: 'new', taxableIncome: 1180000, taxPaid: 142000 },
    { itrType: 'ITR-1', status: 'refund_issued', regime: 'old', taxableIncome: 980000, taxPaid: 120000, refundAmount: 18500 },
    { itrType: 'ITR-1', status: 'filed', regime: 'old', taxableIncome: 910000, taxPaid: 102000 },
  ]),
  ...buildAssessments('cust-002', 'BCDEF2345G', [
    { itrType: 'ITR-2', status: 'documents_pending', regime: 'new', taxableIncome: 2850000, taxPaid: 0 },
    { itrType: 'ITR-2', status: 'processed', regime: 'new', taxableIncome: 2640000, taxPaid: 560000 },
    { itrType: 'ITR-2', status: 'demand_raised', regime: 'old', taxableIncome: 2510000, taxPaid: 480000, demandAmount: 42000 },
    { itrType: 'ITR-2', status: 'filed', regime: 'old', taxableIncome: 2390000, taxPaid: 455000 },
    { itrType: 'ITR-2', status: 'processed', regime: 'old', taxableIncome: 2200000, taxPaid: 410000 },
  ]),
  ...buildAssessments('cust-003', 'CDEFG3456H', [
    { itrType: 'ITR-3', status: 'under_review', regime: 'old', taxableIncome: 4200000, taxPaid: 980000 },
    { itrType: 'ITR-3', status: 'processed', regime: 'old', taxableIncome: 3980000, taxPaid: 920000 },
    { itrType: 'ITR-3', status: 'refund_issued', regime: 'old', taxableIncome: 3650000, taxPaid: 880000, refundAmount: 34000 },
  ]),
  ...buildAssessments('cust-004', 'DEFGH4567J', [
    { itrType: 'ITR-4', status: 'not_started', regime: 'new', taxableIncome: 1650000, taxPaid: 0 },
    { itrType: 'ITR-4', status: 'filed', regime: 'new', taxableIncome: 1520000, taxPaid: 210000 },
    { itrType: 'ITR-4', status: 'processed', regime: 'old', taxableIncome: 1410000, taxPaid: 195000 },
  ]),
  ...buildAssessments('cust-005', 'EFGHI5678K', [
    { itrType: 'ITR-1', status: 'in_progress', regime: 'new', taxableIncome: 820000, taxPaid: 36000 },
    { itrType: 'ITR-1', status: 'processed', regime: 'new', taxableIncome: 760000, taxPaid: 54000 },
  ]),
]

/** Lookup all mock assessments for a customer id. */
export function mockAssessmentsForCustomer(customerId: string): Assessment[] {
  return mockAssessments.filter((a) => a.customerId === customerId)
}
